import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, Plus, X } from "lucide-react";
import type { MatchWithPlayersAndClub, ClubWithMembership, AmericanoTournament } from "../../../shared/schema";
import LiveMatchCard from "@/components/live-match-card";
import LiveAmericanoCard from "@/components/live-americano-card";
import CreateMatchForm from "@/components/create-match-form";
import CreateAmericanoForm from "@/components/create-americano-form";
import { ResponsiveModal } from "@/components/responsive-modal";
import { Button } from "@/components/ui/button";
import { apiRequest } from "@/lib/queryClient";
import { useTranslation } from "react-i18next";

interface MatchesTabProps {
  selectedPlayerId: string | null;
  initialFilter?: MatchesFilter;
}

type MatchesFilter = "mine" | "clubs" | "all";
type CreateTab = "match" | "americano";

export default function MatchesTab({ selectedPlayerId, initialFilter }: MatchesTabProps) {
  const { t } = useTranslation();

  const [searchQuery, setSearchQuery] = useState<string>("");
  const [activeFilter, setActiveFilter] = useState<MatchesFilter>(initialFilter ?? (selectedPlayerId ? "mine" : "all"));
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createTab, setCreateTab] = useState<CreateTab>("match");
  const [isTourActive, setIsTourActive] = useState(false);

  useEffect(() => {
    const handleTourStatus = (e: any) => {
      if (e.detail?.tab === 'matches') {
        setIsTourActive(e.detail.isRunning);
      } else {
        setIsTourActive(false);
      }
    };
    window.addEventListener('padelo-tour-status', handleTourStatus);
    return () => window.removeEventListener('padelo-tour-status', handleTourStatus);
  }, []);

  const { data: activeMatches = [] } = useQuery<MatchWithPlayersAndClub[]>({
    queryKey: ["/api/matches/active"],
    refetchInterval: 5000,
  });

  const { data: activeTournaments = [] } = useQuery<AmericanoTournament[]>({
    queryKey: ["/api/americano/active"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/americano");
      const all: AmericanoTournament[] = await res.json();
      return all.filter(t => t.status === "setup" || t.status === "in_progress");
    },
    refetchInterval: 10000,
  });

  const { data: myClubs = [] } = useQuery<ClubWithMembership[]>({
    queryKey: ["/api/clubs", selectedPlayerId],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/clubs?playerId=${selectedPlayerId}`);
      return response.json();
    },
    enabled: !!selectedPlayerId,
  });
  const myClubIds = new Set(myClubs.filter(c => c.isMember && c.status !== 'pending').map(c => c.id));

  const tabFilteredMatches = activeMatches.filter(match => {
    if (activeFilter === "mine") {
      if (!selectedPlayerId) return false;
      return (
        match.teamAPlayer1?.id === selectedPlayerId ||
        match.teamAPlayer2?.id === selectedPlayerId ||
        match.teamBPlayer1?.id === selectedPlayerId ||
        match.teamBPlayer2?.id === selectedPlayerId
      );
    }
    if (activeFilter === "clubs") {
      if (!selectedPlayerId || myClubIds.size === 0) return false;
      return match.clubId ? myClubIds.has(match.clubId) : false;
    }
    return true;
  });

  const filteredMatches = tabFilteredMatches.filter((match) => {
    if (!searchQuery.trim()) return true;
    const searchGroups = searchQuery.split(/[|;,]/).map(t => t.trim().toLowerCase()).filter(t => t.length > 0);
    if (searchGroups.length === 0) return true;
    const playerNames = [
      match.teamAPlayer1?.name,
      match.teamAPlayer2?.name,
      match.teamBPlayer1?.name,
      match.teamBPlayer2?.name
    ].filter(Boolean).map(name => name!.toLowerCase());
    const clubName = match.club?.name?.toLowerCase() || "";
    return searchGroups.some(group => {
      const requiredWords = group.split(/[\s+]+/).filter(w => w.length > 0);
      return requiredWords.every(word =>
        playerNames.some(name => name.includes(word)) || clubName.includes(word)
      );
    });
  });

  const openCreate = (tab: CreateTab = "match") => {
    setCreateTab(tab);
    setIsCreateModalOpen(true);
  };

  return (
    <div data-testid="matches-tab">
      <section className="p-3 sm:p-4" data-testid="active-matches-section">
        <div className="mb-4">
          {activeMatches.length > 0 ? (
            <>
              <div className="flex items-center gap-2 mb-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                  <input
                    type="text"
                    placeholder={t('matches.tab.search')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-8 py-2 text-sm dark:bg-input border border-gray-300 dark:border-border rounded-lg focus:ring-2 focus:ring-padel-green focus:border-transparent touch-manipulation"
                    data-testid="search-matches-input"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
                <Button
                  id="tour-matches-create"
                  size="icon"
                  className="rounded-full h-10 w-10 shrink-0 bg-padel-green hover:bg-padel-dark-green text-white dark-btn-active shadow-md active:scale-95 transition-transform"
                  onClick={() => openCreate("match")}
                  data-testid="header-create-match"
                >
                  <Plus className="h-6 w-6" />
                </Button>
              </div>

              <div className="flex items-end justify-between">
                <div className="flex space-x-6 text-sm font-medium text-gray-500 overflow-x-auto hide-scrollbar">
                  {([
                    { id: "mine" as MatchesFilter, label: t('history.filter.mine', 'Mes parties') },
                    { id: "clubs" as MatchesFilter, label: t('history.filter.clubs', 'Mes clubs') },
                    { id: "all" as MatchesFilter, label: t('history.filter.all', 'Toutes') },
                  ]).map(tab => {
                    const disabled = (tab.id === "mine" || tab.id === "clubs") && !selectedPlayerId;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => !disabled && setActiveFilter(tab.id)}
                        disabled={disabled}
                        className={`pb-3 whitespace-nowrap border-b-2 transition-colors ${
                          activeFilter === tab.id
                            ? "border-[#001489] text-[#001489] dark-text-active"
                            : disabled
                            ? "border-transparent text-gray-300 cursor-not-allowed"
                            : "border-transparent hover:text-gray-700"
                        }`}
                      >
                        {tab.label}
                      </button>
                    );
                  })}
                </div>
                <span className="pb-3 text-xs text-gray-400 whitespace-nowrap">
                  {filteredMatches.length} {filteredMatches.length === 1 ? t('history.counter.match', 'partie') : t('history.counter.matches', 'parties')}
                </span>
              </div>
            </>
          ) : (
            <div className="flex justify-end mb-3">
              <Button
                id="tour-matches-create"
                size="icon"
                className="rounded-full h-10 w-10 bg-padel-green hover:bg-padel-dark-green text-white dark-btn-active shadow-md active:scale-95 transition-transform"
                onClick={() => openCreate("match")}
                data-testid="header-create-match"
              >
                <Plus className="h-6 w-6" />
              </Button>
            </div>
          )}
        </div>

        {filteredMatches.length > 0 || activeTournaments.length > 0 ? (
          <div className="space-y-4" id="tour-matches-list">
            {activeTournaments.map((tournament) => (
              <LiveAmericanoCard
                key={tournament.id}
                tournament={tournament}
                currentPlayerId={selectedPlayerId}
              />
            ))}
            {filteredMatches.map((match) => (
              <LiveMatchCard key={match.id} match={match} currentPlayerId={selectedPlayerId || undefined} />
            ))}
          </div>
        ) : activeMatches.length > 0 ? (
          <div className="bg-gray-50 dark:bg-muted border border-gray-200 dark:border-border rounded-xl p-6 text-center" data-testid="no-filtered-matches">
            <div className="text-muted-foreground text-sm">
              {searchQuery.trim() ? t('matches.tab.noMatchesTitle') : t('matches.tab.noActiveMatchesTitle')}
            </div>
            <div className="text-muted-foreground text-xs mt-1">
              {searchQuery.trim() ? t('matches.tab.noMatchesDesc') : t('matches.tab.noActiveMatchesDesc')}
            </div>
          </div>
        ) : isTourActive ? (
          <div className="space-y-4">
            <div className="bg-gray-50 dark:bg-muted border border-gray-200 dark:border-border rounded-xl p-6 text-center" data-testid="no-active-matches">
              <div className="text-muted-foreground text-sm">{t('matches.tab.noActiveMatchesTitle')}</div>
              <div className="text-muted-foreground text-xs mt-1">{t('matches.tab.noActiveMatchesDesc')}</div>
            </div>
            <div id="tour-matches-list" className="relative opacity-70 pointer-events-none mt-6">
              <div className="absolute -top-3 left-4 bg-blue-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full z-10 shadow-sm uppercase tracking-wide">
                Exemple
              </div>
              <LiveMatchCard match={{
                id: "fake-match-tutorial",
                clubId: null,
                startTime: new Date().toISOString(),
                endTime: null,
                duration: null,
                isCompleted: true,
                isConfirmed: false,
                teamAPlayer1Id: "demo-p1",
                teamAPlayer2Id: "demo-p2",
                teamBPlayer1Id: "demo-p3",
                teamBPlayer2Id: "demo-p4",
                teamAScore: [6, 4, 6],
                teamBScore: [4, 6, 2],
                createdByPlayerId: "demo-p1",
                winnerId: "A",
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                teamAPlayer1: { id: "demo-p1", name: "Alice M.", eloRating: 1550, matchesPlayed: 10, matchesWon: 6, matchesLost: 4, isPremium: false, isAdmin: false, eloHistory: [], createdAt: new Date().toISOString(), updatedAt: null },
                teamAPlayer2: { id: "demo-p2", name: "Bob P.", eloRating: 1480, matchesPlayed: 8, matchesWon: 4, matchesLost: 4, isPremium: false, isAdmin: false, eloHistory: [], createdAt: new Date().toISOString(), updatedAt: null },
                teamBPlayer1: { id: "demo-p3", name: "Charlie D.", eloRating: 1600, matchesPlayed: 15, matchesWon: 10, matchesLost: 5, isPremium: false, isAdmin: false, eloHistory: [], createdAt: new Date().toISOString(), updatedAt: null },
                teamBPlayer2: { id: "demo-p4", name: "David L.", eloRating: 1420, matchesPlayed: 5, matchesWon: 1, matchesLost: 4, isPremium: false, isAdmin: false, eloHistory: [], createdAt: new Date().toISOString(), updatedAt: null },
                club: null
              } as any} currentPlayerId={undefined} />
            </div>
          </div>
        ) : (
          <div className="bg-gray-50 dark:bg-muted border border-gray-200 dark:border-border rounded-xl p-6 text-center" data-testid="no-active-matches">
            <div className="text-muted-foreground text-sm">{t('matches.tab.noActiveMatchesTitle')}</div>
            <div className="text-muted-foreground text-xs mt-1">{t('matches.tab.noActiveMatchesDesc')}</div>
          </div>
        )}
      </section>

      {/* Create Modal — Match ou Américano */}
      <ResponsiveModal
        open={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
        title={createTab === "match" ? t('matches.tab.create') : "Créer un Américano"}
        description={createTab === "match" ? t('matches.tab.createDesc') : "Configure ton tournoi Américano"}
      >
        {/* Tab switcher */}
        <div className="flex border-b border-gray-200 dark:border-border mb-4 -mt-1">
          <button
            onClick={() => setCreateTab("match")}
            className={`flex-1 pb-2.5 text-sm font-medium border-b-2 transition-colors ${
              createTab === "match"
                ? "border-[#001489] text-[#001489] dark-text-active"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            Match
          </button>
          <button
            onClick={() => setCreateTab("americano")}
            className={`flex-1 pb-2.5 text-sm font-medium border-b-2 transition-colors ${
              createTab === "americano"
                ? "border-[#001489] text-[#001489] dark-text-active"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            Américano
          </button>
        </div>

        {createTab === "match" ? (
          <CreateMatchForm
            playerId={selectedPlayerId}
            onSuccess={() => setIsCreateModalOpen(false)}
          />
        ) : (
          <CreateAmericanoForm
            playerId={selectedPlayerId}
            onSuccess={() => setIsCreateModalOpen(false)}
          />
        )}
      </ResponsiveModal>
    </div>
  );
}
